import type { CSSProperties } from "react";
import type { TourRect } from "./tutorialTargeting";
import { getTourSidebarWidth, getTourViewportInsets } from "./tutorialViewport";

export type TooltipPlacement = "top" | "bottom" | "left" | "right";
export type TooltipSize = "default" | "compact";

const SIZE_PRESETS: Record<TooltipSize, { width: number; height: number }> = {
  default: { width: 340, height: 300 },
  // Wider compact cards wrap less; height is a positioning estimate (real content is often taller).
  compact: { width: 360, height: 300 },
};

const GAP = 14;
const OVERLAP_MARGIN = 10;
const MIN_CARD_HEIGHT = 160;
/** Narrowest acceptable card when squeezing beside a wide centered target. */
const MIN_SIDE_CARD_WIDTH = 180;
/** Absolute floor used when preferSide must still place beside the target. */
const FORCED_SIDE_CARD_WIDTH = 160;

type CardRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

type ScoredStyle = {
  style: CSSProperties;
  score: number;
};

function rectsOverlap(a: CardRect, b: TourRect, margin = OVERLAP_MARGIN): boolean {
  return !(
    a.left + a.width + margin <= b.left ||
    b.left + b.width + margin <= a.left ||
    a.top + a.height + margin <= b.top ||
    b.top + b.height + margin <= a.top
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function styleToCardRect(style: CSSProperties, height: number): CardRect {
  const width = Number(style.width ?? SIZE_PRESETS.default.width);
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;

  if (style.right !== undefined) {
    const right = Number(style.right);
    const left = viewportW - right - width;
    if (style.bottom !== undefined) {
      const bottom = Number(style.bottom);
      return { top: viewportH - bottom - height, left, width, height };
    }
    return { top: Number(style.top ?? 0), left, width, height };
  }

  if (style.bottom !== undefined && style.top === undefined) {
    const top = viewportH - Number(style.bottom) - height;
    return { top, left: Number(style.left ?? 0), width, height };
  }

  return {
    top: Number(style.top ?? 0),
    left: Number(style.left ?? 0),
    width,
    height,
  };
}

function baseCardStyle(
  width: number,
  maxCardHeight: number,
): Pick<CSSProperties, "position" | "width" | "maxHeight" | "overflowY" | "zIndex"> {
  return {
    position: "fixed",
    width,
    maxHeight: maxCardHeight,
    overflowY: "hidden",
    zIndex: 1202,
  };
}

/** Keep the card box fully on-screen so Next stays clickable. */
function fitStyleToViewport(
  style: CSSProperties,
  insets: ReturnType<typeof getTourViewportInsets>,
  estimatedHeight: number,
): CSSProperties {
  const viewportH = window.innerHeight;
  const usable = viewportH - insets.top - insets.bottom;

  if (style.top !== undefined && style.bottom === undefined) {
    const top = Number(style.top);
    const available = viewportH - insets.bottom - top;
    if (available < Math.min(estimatedHeight, MIN_CARD_HEIGHT) && top > insets.top) {
      return {
        ...style,
        top: undefined,
        bottom: insets.bottom,
        maxHeight: Math.min(Number(style.maxHeight ?? usable), usable),
      };
    }
    return {
      ...style,
      maxHeight: Math.min(Number(style.maxHeight ?? available), Math.max(MIN_CARD_HEIGHT, available)),
    };
  }

  if (style.bottom !== undefined && style.top === undefined) {
    const bottom = Number(style.bottom);
    const available = viewportH - insets.top - bottom;
    return {
      ...style,
      maxHeight: Math.min(Number(style.maxHeight ?? available), Math.max(MIN_CARD_HEIGHT, available)),
    };
  }

  return style;
}

function edgeDistance(card: CardRect, target: TourRect): number {
  const cardCenterX = card.left + card.width / 2;
  const cardCenterY = card.top + card.height / 2;
  const targetCenterX = target.left + target.width / 2;
  const targetCenterY = target.top + target.height / 2;
  return Math.hypot(cardCenterX - targetCenterX, cardCenterY - targetCenterY);
}

function preferenceBonus(side: TooltipPlacement, preferred: TooltipPlacement): number {
  if (side === preferred) return -80;
  const order: TooltipPlacement[] = ["bottom", "right", "top", "left"];
  const sideIndex = order.indexOf(side);
  const preferredIndex = order.indexOf(preferred);
  if (sideIndex === -1 || preferredIndex === -1) return 0;
  return Math.abs(sideIndex - preferredIndex) * 12;
}

function buildAdjacentCandidate(
  side: TooltipPlacement,
  rect: TourRect,
  cardWidth: number,
  cardHeight: number,
  insets: ReturnType<typeof getTourViewportInsets>,
  maxCardHeight: number,
  preferred: TooltipPlacement,
): ScoredStyle | null {
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;
  const sidebarEdge = getTourSidebarWidth() + 8;

  let style: CSSProperties | null = null;

  if (side === "right") {
    const left = rect.left + rect.width + GAP;
    const available = viewportW - insets.right - left;
    if (available < MIN_SIDE_CARD_WIDTH) return null;
    const width = Math.min(cardWidth, available);
    const maxTop = viewportH - insets.bottom - cardHeight;
    if (maxTop < insets.top || rect.top > maxTop) {
      // Target is low (e.g. Settings in the sidebar) — pin card to the bottom.
      style = { ...baseCardStyle(width, maxCardHeight), bottom: insets.bottom, left };
    } else {
      const top = clamp(rect.top, insets.top, maxTop);
      style = { ...baseCardStyle(width, maxCardHeight), top, left };
    }
  } else if (side === "left") {
    const minLeft = Math.max(insets.left, sidebarEdge);
    const available = rect.left - GAP - minLeft;
    if (available < MIN_SIDE_CARD_WIDTH) return null;
    const width = Math.min(cardWidth, available);
    const left = rect.left - width - GAP;
    const maxTop = viewportH - insets.bottom - cardHeight;
    if (maxTop < insets.top || rect.top > maxTop) {
      style = { ...baseCardStyle(width, maxCardHeight), bottom: insets.bottom, left };
    } else {
      const top = clamp(rect.top, insets.top, maxTop);
      style = { ...baseCardStyle(width, maxCardHeight), top, left };
    }
  } else if (side === "bottom") {
    const top = rect.top + rect.height + GAP;
    if (top + Math.min(cardHeight, MIN_CARD_HEIGHT) > viewportH - insets.bottom) return null;
    let left = rect.left;
    if (left + cardWidth > viewportW - insets.right) {
      left = viewportW - insets.right - cardWidth;
    }
    left = Math.max(Math.max(insets.left, sidebarEdge), left);
    style = { ...baseCardStyle(cardWidth, maxCardHeight), top, left };
  } else {
    const bottom = viewportH - rect.top + GAP;
    if (bottom + Math.min(cardHeight, MIN_CARD_HEIGHT) > viewportH - insets.top) return null;
    let left = rect.left;
    if (left + cardWidth > viewportW - insets.right) {
      left = viewportW - insets.right - cardWidth;
    }
    left = Math.max(Math.max(insets.left, sidebarEdge), left);
    style = { ...baseCardStyle(cardWidth, maxCardHeight), left, bottom };
  }

  if (!style) return null;

  style = fitStyleToViewport(style, insets, cardHeight);
  const fittedHeight = Math.min(cardHeight, Number(style.maxHeight ?? cardHeight));
  const cardRect = styleToCardRect(style, fittedHeight);
  if (rectsOverlap(cardRect, rect)) return null;

  return {
    style,
    score: edgeDistance(cardRect, rect) + preferenceBonus(side, preferred),
  };
}

function buildCornerCandidates(
  rect: TourRect,
  cardWidth: number,
  cardHeight: number,
  insets: ReturnType<typeof getTourViewportInsets>,
  maxCardHeight: number,
): ScoredStyle[] {
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;
  const sidebarEdge = getTourSidebarWidth() + 16;
  const corners: CSSProperties[] = [
    {
      ...baseCardStyle(cardWidth, maxCardHeight),
      top: insets.top + 8,
      right: insets.right,
    },
    {
      ...baseCardStyle(cardWidth, maxCardHeight),
      bottom: insets.bottom,
      right: insets.right,
    },
    {
      ...baseCardStyle(cardWidth, maxCardHeight),
      bottom: insets.bottom,
      left: sidebarEdge,
    },
    {
      ...baseCardStyle(cardWidth, maxCardHeight),
      top: insets.top + 8,
      left: sidebarEdge,
    },
    {
      ...baseCardStyle(cardWidth, maxCardHeight),
      top: clamp(rect.top, insets.top, Math.max(insets.top, viewportH - insets.bottom - cardHeight)),
      left: clamp(
        sidebarEdge,
        insets.left,
        viewportW - insets.right - cardWidth,
      ),
    },
  ];

  const scored: ScoredStyle[] = [];
  for (const raw of corners) {
    const style = fitStyleToViewport(raw, insets, cardHeight);
    const fittedHeight = Math.min(cardHeight, Number(style.maxHeight ?? cardHeight));
    const cardRect = styleToCardRect(style, fittedHeight);
    if (rectsOverlap(cardRect, rect)) continue;
    scored.push({
      style,
      score: edgeDistance(cardRect, rect) + 120,
    });
  }
  return scored;
}

function sidePlacementOrder(
  preferred: TooltipPlacement,
): Array<"left" | "right"> {
  return preferred === "left" ? ["left", "right"] : ["right", "left"];
}

function buildForcedSideStyle(
  preferred: TooltipPlacement,
  rect: TourRect,
  cardWidth: number,
  cardHeight: number,
  insets: ReturnType<typeof getTourViewportInsets>,
  maxCardHeight: number,
): CSSProperties {
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;
  const sidebarEdge = getTourSidebarWidth() + 8;
  const gap = 8;
  const leftGutter = Math.max(0, rect.left - Math.max(insets.left, sidebarEdge) - gap);
  const rightGutter = Math.max(0, viewportW - insets.right - (rect.left + rect.width) - gap);

  let side: "left" | "right" =
    preferred === "left" || preferred === "right" ? preferred : "right";
  if (side === "left" && leftGutter < FORCED_SIDE_CARD_WIDTH && rightGutter > leftGutter) {
    side = "right";
  } else if (
    side === "right" &&
    rightGutter < FORCED_SIDE_CARD_WIDTH &&
    leftGutter > rightGutter
  ) {
    side = "left";
  } else if (side === "left" && rightGutter > leftGutter + 40) {
    // Prefer the roomier gutter when the preferred side is clearly tighter.
    side = "right";
  } else if (side === "right" && leftGutter > rightGutter + 40) {
    side = "left";
  }

  const maxTop = viewportH - insets.bottom - Math.min(cardHeight, MIN_CARD_HEIGHT);
  const top =
    maxTop < insets.top || rect.top > maxTop
      ? undefined
      : clamp(rect.top, insets.top, Math.max(insets.top, maxTop));
  const vertical =
    top === undefined
      ? { bottom: insets.bottom }
      : { top };

  if (side === "right") {
    const left = rect.left + rect.width + gap;
    const width = Math.min(
      cardWidth,
      Math.max(FORCED_SIDE_CARD_WIDTH, viewportW - insets.right - left),
    );
    const clampedLeft = Math.min(left, viewportW - insets.right - width);
    return {
      ...baseCardStyle(width, maxCardHeight),
      ...vertical,
      left: Math.max(sidebarEdge, clampedLeft),
    };
  }

  const width = Math.min(cardWidth, Math.max(FORCED_SIDE_CARD_WIDTH, leftGutter));
  const left = Math.max(
    Math.max(insets.left, sidebarEdge),
    rect.left - width - gap,
  );
  return {
    ...baseCardStyle(width, maxCardHeight),
    ...vertical,
    left,
  };
}

function placementOrder(placement: TooltipPlacement | undefined): TooltipPlacement[] {
  const preferred = placement ?? "bottom";
  if (preferred === "bottom") return ["bottom", "right", "top", "left"];
  if (preferred === "top") return ["top", "bottom", "right", "left"];
  if (preferred === "right") return ["right", "left", "bottom", "top"];
  return ["left", "right", "bottom", "top"];
}

function pickBest(candidates: ScoredStyle[]): CSSProperties | null {
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => a.score - b.score);
  return candidates[0]?.style ?? null;
}

export function computeTooltipStyle(
  rect: TourRect,
  placement: TooltipPlacement | undefined,
  options?: { size?: TooltipSize; preferSide?: boolean },
): CSSProperties {
  const insets = getTourViewportInsets();
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;
  const size = options?.size ?? "default";
  const preset = SIZE_PRESETS[size];
  const cardWidth = Math.min(preset.width, viewportW - insets.left - insets.right - 8);
  const maxCardHeight = viewportH - insets.top - insets.bottom - 16;
  const cardHeight = Math.min(preset.height, maxCardHeight);
  const preferred = placement ?? "bottom";
  const preferSide = Boolean(options?.preferSide);

  const sides = preferSide ? sidePlacementOrder(preferred) : placementOrder(preferred);
  const adjacent: ScoredStyle[] = [];
  for (const side of sides) {
    const candidate = buildAdjacentCandidate(
      side,
      rect,
      cardWidth,
      cardHeight,
      insets,
      maxCardHeight,
      preferred,
    );
    if (candidate) adjacent.push(candidate);
  }

  const bestAdjacent = pickBest(adjacent);
  if (bestAdjacent) return bestAdjacent;

  if (preferSide) {
    return fitStyleToViewport(
      buildForcedSideStyle(
        preferred,
        rect,
        cardWidth,
        cardHeight,
        insets,
        maxCardHeight,
      ),
      insets,
      cardHeight,
    );
  }

  const corner = pickBest(
    buildCornerCandidates(rect, cardWidth, cardHeight, insets, maxCardHeight),
  );
  if (corner) return corner;

  return fitStyleToViewport(
    {
      ...baseCardStyle(cardWidth, maxCardHeight),
      top: insets.top + 8,
      right: insets.right,
    },
    insets,
    cardHeight,
  );
}

/** Fallback card position when the spotlight target cannot be measured. */
export function fallbackTooltipStyle(size: TooltipSize = "default"): CSSProperties {
  const insets = getTourViewportInsets();
  const preset = SIZE_PRESETS[size];
  const usable = window.innerHeight - insets.top - insets.bottom - 16;
  return {
    position: "fixed",
    bottom: insets.bottom,
    left: getTourSidebarWidth() + 16,
    width: preset.width,
    maxHeight: Math.min(preset.height + 40, usable, 380),
    overflowY: "hidden",
    zIndex: 1202,
  };
}
